import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Download, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Badge, Button, EmptyState, Field, Input, Section, Select, Spinner } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { api, ApiError } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { IysList, SubscriberList, SuppressionRow } from '../lib/types';

type SubStatus = 'subscribed' | 'pending' | 'unsubscribed' | 'all';
type Channel = 'email' | 'sms' | 'whatsapp';

/**
 * Pazarlama → Aboneler ve izinler (F6-01): bülten aboneleri, engel listesi, İYS'ye bildirilecek kayıtlar.
 * Adresler kişisel veri yetkisi olmadan maskeli gelir (sunucuda).
 */
export function MarketingAudiencePage() {
  return (
    <PageFrame helpKey="page.marketing_audience">
      <div className="space-y-8">
        <IysSection />
        <SubscribersSection />
        <SuppressionsSection />
      </div>
    </PageFrame>
  );
}

function SubscribersSection() {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const [status, setStatus] = useState<SubStatus>('subscribed');
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState<string | null>(null);
  const list = useQuery({
    queryKey: ['subscribers', status, q, cursor],
    queryFn: () => {
      const qs = new URLSearchParams({ status, limit: '50' });
      if (q.trim()) qs.set('q', q.trim());
      if (cursor) qs.set('cursor', cursor);
      return api.get<SubscriberList>(`/v1/admin/marketing/subscribers?${qs.toString()}`);
    },
  });
  const d = list.data;
  return (
    <Section
      title={t('admin.audience.subscribersTitle')}
      description={t('admin.audience.subscribersLead')}
      action={<HelpTip k="audience.subscribers" />}
    >
      <div className="flex flex-wrap items-end gap-4">
        <Field label={t('admin.audience.status')} help={<HelpTip k="audience.status" />} className="w-56">
          {(p) => (
            <Select
              {...p}
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as SubStatus);
                setCursor(null);
              }}
            >
              {(['subscribed', 'pending', 'unsubscribed', 'all'] as const).map((s) => (
                <option key={s} value={s}>
                  {t(`admin.audience.statuses.${s}`)}
                  {d && s !== 'all' ? ` (${d.counts[s]})` : ''}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {can('customers.pii.read') && (
          <Field label={t('admin.audience.search')} help={<HelpTip k="audience.search" />} className="w-64">
            {(p) => (
              <Input
                {...p}
                type="search"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setCursor(null);
                }}
              />
            )}
          </Field>
        )}
      </div>
      {!d ? (
        <Spinner label={t('common.loading')} />
      ) : !d.items.length ? (
        <EmptyState
          title={t('admin.audience.noSubscribers')}
          description={t('admin.audience.noSubscribersLead')}
        />
      ) : (
        <div className="mt-4 overflow-x-auto rounded-md border border-line">
          <table className="w-full text-sm" aria-label={t('admin.audience.subscribersTitle')}>
            <thead className="bg-surface-2 text-ink-2">
              <tr>
                <th className="px-3 py-2 text-start font-medium">{t('admin.audience.colEmail')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.audience.colStatus')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.audience.colConsent')}</th>
                <th className="px-3 py-2 text-end font-medium">
                  <span className="sr-only">{t('common.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {d.items.map((s) => (
                <SubscriberRow key={s.id} s={s} formatDate={formatDate} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {d?.nextCursor && (
        <Button className="mt-3" size="sm" onClick={() => setCursor(d.nextCursor)}>
          {t('common.next')}
        </Button>
      )}
    </Section>
  );
}

function SubscriberRow({
  s,
  formatDate,
}: {
  s: SubscriberList['items'][number];
  formatDate: (d: string, withTime?: boolean) => string;
}) {
  const t = useT();
  const { can } = usePermissions();
  const unsub = useApiMutation(() => api.post(`/v1/admin/marketing/subscribers/${s.id}/unsubscribe`, {}), {
    invalidate: [['subscribers'], ['suppressions'], ['iys']],
    success: t('admin.audience.unsubscribedDone'),
  });
  const remove = useApiMutation(() => api.del(`/v1/admin/marketing/subscribers/${s.id}`), {
    invalidate: [['subscribers']],
    success: t('admin.audience.deletedDone'),
  });
  return (
    <tr className="border-t border-line align-top">
      <td className="px-3 py-2">
        {s.email}
        {s.name && <span className="block text-xs text-ink-3">{s.name}</span>}
        {s.isMember && <span className="block text-xs text-ink-3">{t('admin.audience.member')}</span>}
      </td>
      <td className="px-3 py-2">
        <Badge tone={s.status === 'subscribed' ? 'moss' : s.status === 'pending' ? 'amber' : 'neutral'}>
          {t(`admin.audience.statuses.${s.status}`)}
        </Badge>
      </td>
      <td className="px-3 py-2 text-ink-2">
        {s.consentedAt ? formatDate(s.consentedAt, true) : '—'}
        <span className="block text-xs text-ink-3">
          {t(`admin.audience.sources.${s.source}`)} · {s.consentTextVersion}
        </span>
      </td>
      <td className="px-3 py-2 text-end">
        {can('marketing.audience.manage') && (
          <div className="flex flex-wrap justify-end gap-2">
            {s.status !== 'unsubscribed' && (
              <ConfirmButton
                message={t('admin.audience.unsubscribeConfirm')}
                onConfirm={() => unsub.mutateAsync(undefined)}
                variant="secondary"
              >
                {t('admin.audience.unsubscribe')}
              </ConfirmButton>
            )}
            <ConfirmButton
              message={t('admin.audience.deleteConfirm')}
              onConfirm={() => remove.mutateAsync(undefined)}
            >
              <Trash2 className="h-4 w-4" aria-hidden />
              {t('common.delete')}
            </ConfirmButton>
            <HelpTip k="audience.subscriber_actions" />
          </div>
        )}
      </td>
    </tr>
  );
}

function SuppressionsSection() {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const [channel, setChannel] = useState<Channel | ''>('');
  const [form, setForm] = useState<{ channel: Channel; address: string; note: string }>({
    channel: 'email',
    address: '',
    note: '',
  });
  const list = useQuery({
    queryKey: ['suppressions', channel],
    queryFn: () =>
      api.get<SuppressionRow[]>(
        `/v1/admin/marketing/suppressions?limit=200${channel ? `&channel=${channel}` : ''}`,
      ),
  });
  const add = useApiMutation(
    () =>
      api.post('/v1/admin/marketing/suppressions', {
        channel: form.channel,
        address: form.address.trim(),
        note: form.note.trim() || undefined,
      }),
    {
      invalidate: [['suppressions'], ['subscribers'], ['iys']],
      success: t('admin.audience.suppressedDone'),
      onSuccess: () => setForm((f) => ({ ...f, address: '', note: '' })),
    },
  );
  return (
    <Section
      title={t('admin.audience.suppressionsTitle')}
      description={t('admin.audience.suppressionsLead')}
      action={<HelpTip k="audience.suppressions" />}
    >
      {can('marketing.audience.manage') && (
        <form
          className="mb-5 flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate(undefined);
          }}
        >
          <Field label={t('admin.audience.channel')} help={<HelpTip k="audience.channel" />} className="w-40">
            {(p) => (
              <Select
                {...p}
                value={form.channel}
                onChange={(e) => setForm((f) => ({ ...f, channel: e.target.value as Channel }))}
              >
                {(['email', 'sms', 'whatsapp'] as const).map((c) => (
                  <option key={c} value={c}>
                    {t(`admin.audience.channels.${c}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('admin.audience.address')} help={<HelpTip k="audience.address" />} className="w-64">
            {(p) => (
              <Input
                {...p}
                required
                value={form.address}
                onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
              />
            )}
          </Field>
          <Field label={t('admin.audience.note')} help={<HelpTip k="audience.note" />} className="w-64">
            {(p) => (
              <Input
                {...p}
                value={form.note}
                onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              />
            )}
          </Field>
          <Button type="submit" loading={add.isPending}>
            {t('admin.audience.suppress')}
          </Button>
          <HelpTip k="audience.suppress" />
        </form>
      )}
      <Field
        label={t('admin.audience.channelFilter')}
        help={<HelpTip k="audience.channel_filter" />}
        className="w-56"
      >
        {(p) => (
          <Select {...p} value={channel} onChange={(e) => setChannel(e.target.value as Channel | '')}>
            <option value="">{t('admin.audience.allChannels')}</option>
            {(['email', 'sms', 'whatsapp'] as const).map((c) => (
              <option key={c} value={c}>
                {t(`admin.audience.channels.${c}`)}
              </option>
            ))}
          </Select>
        )}
      </Field>
      {!list.data ? (
        <Spinner label={t('common.loading')} />
      ) : !list.data.length ? (
        <EmptyState
          title={t('admin.audience.noSuppressions')}
          description={t('admin.audience.noSuppressionsLead')}
        />
      ) : (
        <div className="mt-4 overflow-x-auto rounded-md border border-line">
          <table className="w-full text-sm" aria-label={t('admin.audience.suppressionsTitle')}>
            <thead className="bg-surface-2 text-ink-2">
              <tr>
                <th className="px-3 py-2 text-start font-medium">{t('admin.audience.address')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.audience.colReason')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.audience.colDate')}</th>
                <th className="px-3 py-2 text-end font-medium">
                  <span className="sr-only">{t('common.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((r) => (
                <SuppressionRowView key={r.id} r={r} date={formatDate(r.createdAt, true)} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

function SuppressionRowView({ r, date }: { r: SuppressionRow; date: string }) {
  const t = useT();
  const { can } = usePermissions();
  const remove = useApiMutation(() => api.del(`/v1/admin/marketing/suppressions/${r.id}`), {
    invalidate: [['suppressions']],
    success: t('admin.audience.unsuppressedDone'),
  });
  return (
    <tr className="border-t border-line align-top">
      <td className="px-3 py-2">
        {r.address}
        <span className="block text-xs text-ink-3">{t(`admin.audience.channels.${r.channel}`)}</span>
      </td>
      <td className="px-3 py-2">
        {t(`admin.audience.reasons.${r.reason}`)}
        {r.note && <span className="block text-xs text-ink-3">{r.note}</span>}
      </td>
      <td className="px-3 py-2 text-ink-2">{date}</td>
      <td className="px-3 py-2 text-end">
        {can('marketing.audience.manage') && (
          <span className="inline-flex items-center gap-1">
            <ConfirmButton
              message={t('admin.audience.unsuppressConfirm')}
              onConfirm={() => remove.mutateAsync(undefined)}
              variant="secondary"
            >
              {t('admin.audience.unsuppress')}
            </ConfirmButton>
            <HelpTip k="audience.unsuppress" />
          </span>
        )}
      </td>
    </tr>
  );
}

function IysSection() {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const [state, setState] = useState<'open' | 'sent' | 'reported' | 'failed' | 'all'>('open');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const list = useQuery({
    queryKey: ['iys', state],
    queryFn: () => api.get<IysList>(`/v1/admin/marketing/iys?state=${state}&limit=500`),
  });
  const mark = useApiMutation(
    () => api.post<{ updated: number }>('/v1/admin/marketing/iys/reported', { ids: [...selected] }),
    {
      invalidate: [['iys']],
      success: t('admin.audience.iysMarked'),
      onSuccess: () => setSelected(new Set()),
    },
  );
  const download = async () => {
    setBusy(true);
    try {
      const { blob, fileName } = await api.blob('/v1/admin/marketing/iys/export');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName ?? 'iys-kayitlari.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      if (!(e instanceof ApiError && e.code === 'step_up_required'))
        toast.error(e instanceof ApiError ? e.title : String(e));
    } finally {
      setBusy(false);
    }
  };
  const d = list.data;
  const manage = can('marketing.audience.manage');
  return (
    <Section
      title={t('admin.audience.iysTitle')}
      description={t('admin.audience.iysLead')}
      action={<HelpTip k="audience.iys" />}
    >
      {!d ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-3 text-sm">
            {d.connected ? (
              <Badge tone="moss">{t('admin.audience.iysConnected')}</Badge>
            ) : (
              <span className="inline-flex items-center gap-2">
                <Badge tone="amber">{t('admin.audience.iysSetup')}</Badge>
                <Link to="/entegrasyonlar" className="text-pine underline underline-offset-2">
                  {t('admin.audience.iysSetupLink')}
                </Link>
              </span>
            )}
            <span>{t('admin.audience.iysPending', { count: d.counts.pending })}</span>
            {d.counts.overdue > 0 && (
              <Badge tone="rose">{t('admin.audience.iysOverdue', { count: d.counts.overdue })}</Badge>
            )}
            {d.counts.failed > 0 && (
              <Badge tone="rose">{t('admin.audience.iysFailed', { count: d.counts.failed })}</Badge>
            )}
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <Field
              label={t('admin.audience.iysFilter')}
              help={<HelpTip k="audience.iys_filter" />}
              className="w-56"
            >
              {(p) => (
                <Select
                  {...p}
                  value={state}
                  onChange={(e) => {
                    setState(e.target.value as typeof state);
                    setSelected(new Set());
                  }}
                >
                  {(['open', 'sent', 'reported', 'failed', 'all'] as const).map((s) => (
                    <option key={s} value={s}>
                      {t(`admin.audience.iysFilters.${s}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            {manage && can('customers.pii.read') && (
              <span className="inline-flex items-center gap-1">
                <Button onClick={() => void download()} loading={busy}>
                  <Download className="h-4 w-4" aria-hidden />
                  {t('admin.audience.iysDownload')}
                </Button>
                <HelpTip k="audience.iys_download" />
              </span>
            )}
            {manage && (
              <span className="inline-flex items-center gap-1">
                <Button
                  disabled={!selected.size}
                  loading={mark.isPending}
                  onClick={() => mark.mutate(undefined)}
                >
                  {t('admin.audience.iysMark', { count: selected.size })}
                </Button>
                <HelpTip k="audience.iys_mark" />
              </span>
            )}
          </div>
          {!d.items.length ? (
            <EmptyState title={t('admin.audience.iysEmpty')} description={t('admin.audience.iysEmptyLead')} />
          ) : (
            <div className="mt-4 overflow-x-auto rounded-md border border-line">
              <table className="w-full text-sm" aria-label={t('admin.audience.iysTitle')}>
                <thead className="bg-surface-2 text-ink-2">
                  <tr>
                    {manage && (
                      <th className="px-3 py-2 text-start font-medium">
                        <span className="sr-only">{t('admin.audience.select')}</span>
                      </th>
                    )}
                    <th className="px-3 py-2 text-start font-medium">{t('admin.audience.address')}</th>
                    <th className="px-3 py-2 text-start font-medium">{t('admin.audience.colIys')}</th>
                    <th className="px-3 py-2 text-start font-medium">{t('admin.audience.colDue')}</th>
                    <th className="px-3 py-2 text-start font-medium">{t('admin.audience.colState')}</th>
                  </tr>
                </thead>
                <tbody>
                  {d.items.map((r) => {
                    const open = r.state === 'pending' || r.state === 'failed';
                    return (
                      <tr key={r.id} className="border-t border-line align-top">
                        {manage && (
                          <td className="px-3 py-2">
                            {open && (
                              <input
                                type="checkbox"
                                aria-label={t('admin.audience.selectRecord', { address: r.recipient })}
                                checked={selected.has(r.id)}
                                onChange={(e) =>
                                  setSelected((prev) => {
                                    const next = new Set(prev);
                                    if (e.target.checked) next.add(r.id);
                                    else next.delete(r.id);
                                    return next;
                                  })
                                }
                              />
                            )}
                          </td>
                        )}
                        <td className="px-3 py-2">
                          {r.recipient}
                          <span className="block text-xs text-ink-3">
                            {t(`admin.audience.recipientTypes.${r.recipientType}`)}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          {t(`admin.audience.iysTypes.${r.type}`)} ·{' '}
                          <strong>{t(`admin.audience.iysStatuses.${r.status}`)}</strong>
                          <span className="block text-xs text-ink-3">
                            {formatDate(r.consentAt, true)} · {r.source}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          {formatDate(r.dueAt)}
                          {r.overdue && (
                            <span className="mt-0.5 block">
                              <Badge tone="rose">{t('admin.audience.overdue')}</Badge>
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {t(`admin.audience.iysStates.${r.state}`)}
                          {r.lastError && <span className="block text-xs text-rose">{r.lastError}</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Section>
  );
}
